import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// V5 收敛：AI 策略 / 维度介绍图 / 企业当前策略入口已隐藏（路由保留，需要时手敲 URL）
const NAV_BY_ROLE = {
  superadmin: [
    { path: '/admin', label: '数据看板', icon: '📊', exact: true },
    { path: '/admin/assessment-data', label: '测评数据', icon: '⏱️' },
    { path: '/admin/questions', label: '题库管理', icon: '📝' },
    { path: '/admin/dimensions', label: '维度管理', icon: '🎯' },
    { path: '/admin/risk-templates', label: '风险模板', icon: '⚠️' },
    { path: '/admin/prompts', label: 'Prompt 配置', icon: '🤖' },
    { path: '/admin/enterprises', label: '企业管理', icon: '🏢' },
    { path: '/admin/users', label: '用户管理', icon: '👥' },
  ],
  admin: [
    { path: '/admin', label: '数据看板', icon: '📊', exact: true },
    { path: '/admin/assessment-data', label: '测评数据', icon: '⏱️' },
    { path: '/admin/questions', label: '题库管理', icon: '📝' },
    { path: '/admin/dimensions', label: '维度管理', icon: '🎯' },
    { path: '/admin/risk-templates', label: '风险模板', icon: '⚠️' },
    { path: '/admin/prompts', label: 'Prompt 配置', icon: '🤖' },
    { path: '/admin/enterprises', label: '企业管理', icon: '🏢' },
    { path: '/admin/users', label: '用户管理', icon: '👥' },
  ],
  enterprise_admin: [
    { path: '/admin', label: '数据看板', icon: '📊', exact: true },
    { path: '/admin/users', label: '成员管理', icon: '👥' },
    { path: '/admin/form-config', label: '信息收集配置', icon: '📋' },
  ],
};

const ROLE_LABEL = {
  superadmin: '超级管理员',
  admin: '系统管理员',
  enterprise_admin: '企业管理员',
};

export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const navItems = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.admin;
  const isActive = (item) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f1f5f9' }}>
      {/* 侧边栏 */}
      <aside style={{
        width: collapsed ? 60 : 220,
        background: '#1a3a6b',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{
          padding: collapsed ? '20px 0' : '20px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          {!collapsed && (
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: 1 }}>
              AIQ <span style={{ color: '#93c5fd', fontWeight: 400, fontSize: 13 }}>管理后台</span>
            </span>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#93c5fd', fontSize: 16, marginLeft: collapsed ? 0 : 'auto' }}
          >
            {collapsed ? '→' : '←'}
          </button>
        </div>

        {/* 导航 */}
        <nav style={{ flex: 1, padding: '12px 0' }}>
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                display: 'flex', alignItems: 'center',
                gap: 10, padding: collapsed ? '12px 0' : '12px 20px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                textDecoration: 'none',
                color: isActive(item) ? '#fff' : 'rgba(255,255,255,0.65)',
                background: isActive(item) ? 'rgba(255,255,255,0.12)' : 'transparent',
                borderLeft: isActive(item) ? '3px solid #60a5fa' : '3px solid transparent',
                fontSize: 14,
                fontWeight: isActive(item) ? 600 : 400,
                transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* 返回个人中心 */}
        <div style={{ padding: collapsed ? '8px 0' : '8px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={() => navigate('/profile')}
            title="返回个人中心"
            style={{
              width: '100%', display: 'flex', alignItems: 'center',
              gap: 8, padding: collapsed ? '8px 0' : '8px 10px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, cursor: 'pointer', color: '#93c5fd', fontSize: 13,
            }}
          >
            <span>←</span>
            {!collapsed && <span>返回个人中心</span>}
          </button>
        </div>

        {/* 用户信息 */}
        <div style={{
          padding: collapsed ? '16px 0' : '16px 20px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#2d5aa0', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}>
            {(user?.display_name || user?.email || 'A')[0].toUpperCase()}
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ color: '#fff', fontSize: 12, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.display_name || user?.email || '管理员'}
              </p>
              <button onClick={handleLogout} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#93c5fd', fontSize: 11, padding: 0, marginTop: 2,
              }}>退出登录</button>
            </div>
          )}
        </div>
      </aside>

      {/* 主内容 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100vh', overflow: 'hidden' }}>
        {/* 顶栏 */}
        <header style={{
          background: '#fff', borderBottom: '1px solid #e2e8f0',
          padding: '16px 28px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexShrink: 0,
        }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#1a3a6b', margin: 0 }}>
            {title || 'AIQ 管理后台'}
          </h1>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            {ROLE_LABEL[user?.role] || '管理员'} · {user?.email || '未绑定邮箱'}
          </div>
        </header>

        {/* 页面内容 — 独立滚动 */}
        <main style={{ flex: 1, padding: '28px', overflowY: 'auto', overflowX: 'hidden' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
